import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { TagComponent } from '@pedix-workspace/angular-ui-alert';
import { IconInfoComponent } from '@pedix-workspace/angular-ui-icons';
import { NgbDropdown, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import {
  getEffectivePromotionFamilies,
  PromotionInput,
} from '@pedix-workspace/shared-promotions';
import { SUPPORTED_LANGUAGES } from '@pedix-workspace/utils';

type BreakdownLine = {
  labelKey: string;
  labelParams: Record<string, unknown>;
  percentage: number;
};

type FamilySectionViewModel =
  | { mode: 'none' }
  | { mode: 'literal'; key: string; params: Record<string, unknown> }
  | {
      mode: 'grouped';
      headerKey: string;
      headerParams: Record<string, unknown>;
      breakdownLines: BreakdownLine[];
      disclaimerKey?: string;
    };

type VisibleFamilySection = Exclude<FamilySectionViewModel, { mode: 'none' }>;

@Component({
  selector: 'pxw-product-details-promotion-tags',
  templateUrl: './product-details-promotion-tags.component.html',
  styleUrls: ['./product-details-promotion-tags.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    TagComponent,
    IconInfoComponent,
    NgbDropdown,
    NgbDropdownMenu,
    NgbDropdownToggle,
  ],
})
export class ProductDetailsPromotionTagsComponent {
  promotions = input<PromotionInput[]>([]);
  selectedLanguage = input<SUPPORTED_LANGUAGES>('es');

  private families = computed(() =>
    getEffectivePromotionFamilies(this.promotions(), this.selectedLanguage()),
  );

  private buyXPayYSection = computed<FamilySectionViewModel>(() => {
    const buyXPayYOffers = this.families().buyXPayYOffers;
    if (buyXPayYOffers.length === 0) {
      return { mode: 'none' };
    }
    if (buyXPayYOffers.length === 1) {
      const offer = buyXPayYOffers[0];
      return {
        mode: 'literal',
        key: 'promotionTag.buyXPayY',
        params: { buy: offer.buy, pay: offer.pay },
      };
    }
    return {
      mode: 'grouped',
      headerKey: 'promotionTag.buyXPayYGrouped',
      headerParams: { maxPercentage: Math.max(0, ...buyXPayYOffers.map(c => c.averagePercentage)) },
      breakdownLines: buyXPayYOffers.map(offer => ({
        labelKey: 'promotionTag.buyXPayYBreakdownLabel',
        labelParams: { buy: offer.buy, pay: offer.pay },
        percentage: offer.averagePercentage,
      })),
    };
  });

  private progressiveSection = computed<FamilySectionViewModel>(() => {
    const tiers = this.families().progressiveTiers;
    if (tiers.length === 0) {
      return { mode: 'none' };
    }
    if (tiers.length === 1) {
      const tier = tiers[0];
      return {
        mode: 'literal',
        key: 'promotionTag.progressiveTierLiteral',
        params: { quantity: tier.quantity, percentage: tier.percentage },
      };
    }
    return {
      mode: 'grouped',
      headerKey: 'promotionTag.progressiveGrouped',
      headerParams: { maxPercentage: Math.max(0, ...tiers.map(t => t.percentage)) },
      breakdownLines: tiers.map(tier => ({
        labelKey: 'promotionTag.progressiveTierFrom',
        labelParams: { quantity: tier.quantity },
        percentage: tier.percentage,
      })),
    };
  });

  private nthUnitSection = computed<FamilySectionViewModel>(() => {
    const nthUnits = this.families().nthUnits;
    if (nthUnits.length === 0) {
      return { mode: 'none' };
    }
    if (nthUnits.length === 1) {
      const nth = nthUnits[0];
      return {
        mode: 'literal',
        key: 'promotionTag.nthUnit',
        params: { n: nth.nthUnit, ordinal: nth.ordinal, percentage: nth.discountPercentage },
      };
    }
    return {
      mode: 'grouped',
      headerKey: 'promotionTag.nthUnitGrouped',
      headerParams: {},
      breakdownLines: nthUnits.map(nth => ({
        labelKey: 'promotionTag.nthUnitBreakdownLabel',
        labelParams: { n: nth.nthUnit, ordinal: nth.ordinal },
        percentage: nth.discountPercentage,
      })),
      disclaimerKey: 'promotionTag.nthUnitGroupedDisclaimer',
    };
  });

  sections = computed<VisibleFamilySection[]>(() =>
    [this.buyXPayYSection(), this.nthUnitSection(), this.progressiveSection()].filter(
      (section): section is VisibleFamilySection => section.mode !== 'none',
    ),
  );
}
